import { PauseIcon, PlayIcon, StopIcon } from "@heroicons/react/24/solid";
import * as Common from "@sp-crm/core";
import { useQueryClient } from "@tanstack/react-query";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { DangerButton } from "components/ui/danger-button";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { TextArea } from "components/ui/textarea";
import { formatElapsed, useTimer } from "contexts/timer-context";
import { useInvalidateEntityNotes } from "hooks/use-invalidate-entity-notes";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { ManualTimeEntryForm } from "./manual-time-entry-form";
import { RecentTimeEntries } from "./recent-time-entries";

interface TimeTrackingPanelProps {
    clientId: Common.ClientId;
    clientName: string;
    onDismiss: () => void;
}

export const TimeTrackingPanel: React.FC<TimeTrackingPanelProps> = ({
    clientId,
    clientName,
    onDismiss,
}) => {
    const {
        timerState,
        elapsedSeconds,
        clientId: timerClientId,
        clientName: timerClientName,
        startTimer,
        pauseTimer,
        resumeTimer,
        stopTimer,
        resetTimer,
        saveEntry,
    } = useTimer();
    const isTimerForCurrentClient = timerClientId === clientId;
    const queryClient = useQueryClient();
    const invalidateNotes = useInvalidateEntityNotes();
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const invalidateEntries = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [["timeEntries"]] });
        invalidateNotes();
    }, [queryClient, invalidateNotes]);

    const handleStart = useCallback(async () => {
        if (timerState !== "idle" && !isTimerForCurrentClient) {
            const confirmed = await fancyConfirm(
                "Timer already running",
                `You have a timer running for ${timerClientName} (${formatElapsed(elapsedSeconds)}). Starting a new timer will discard it.`,
                "Discard & start",
                "Cancel",
            );
            if (!confirmed) return;
        }
        await startTimer(clientId, clientName);
        onDismiss();
    }, [
        clientId,
        clientName,
        startTimer,
        onDismiss,
        timerState,
        isTimerForCurrentClient,
        timerClientName,
        elapsedSeconds,
    ]);

    const handleSave = useCallback(async () => {
        if (!note.trim()) return;
        setSaving(true);
        try {
            await saveEntry(note);
            setNote("");
            invalidateEntries();
        } finally {
            setSaving(false);
        }
    }, [note, saveEntry, invalidateEntries]);

    return (
        <Panel
            type={PanelType.large}
            headerText="Track time"
            onDismiss={onDismiss}
            isOpen={true}>
            <div className="space-y-6">
                {timerState !== "idle" && !isTimerForCurrentClient ? (
                    <div className="text-center space-y-3">
                        <div className="text-sm text-gray-600">
                            Timer active for{" "}
                            <Link
                                to={`/clients/show/${timerClientId}`}
                                className="font-medium text-brand-600 hover:text-brand-800">
                                {timerClientName}
                            </Link>
                        </div>
                        <PrimaryButton onClick={handleStart}>
                            Start timer for {clientName}
                        </PrimaryButton>
                    </div>
                ) : (
                    <>
                        <div className="text-center">
                            <div className="text-4xl font-semibold tracking-tight [font-variant-numeric:tabular-nums] text-gray-900 mb-4">
                                {formatElapsed(elapsedSeconds)}
                            </div>
                            <div className="flex justify-center space-x-3">
                                {timerState === "idle" && (
                                    <PrimaryButton onClick={handleStart}>
                                        Start
                                    </PrimaryButton>
                                )}
                                {timerState === "running" && (
                                    <>
                                        <SecondaryButton
                                            className="items-center"
                                            onClick={pauseTimer}>
                                            <PauseIcon className="h-4 w-4 mr-1" />
                                            Pause
                                        </SecondaryButton>
                                        <DangerButton
                                            className="items-center"
                                            onClick={stopTimer}>
                                            <StopIcon className="h-4 w-4 mr-1" />
                                            Stop
                                        </DangerButton>
                                    </>
                                )}
                                {timerState === "paused" && (
                                    <>
                                        <PrimaryButton
                                            className="items-center"
                                            onClick={resumeTimer}>
                                            <PlayIcon className="h-4 w-4 mr-1" />
                                            Resume
                                        </PrimaryButton>
                                        <DangerButton
                                            className="items-center"
                                            onClick={stopTimer}>
                                            <StopIcon className="h-4 w-4 mr-1" />
                                            Stop
                                        </DangerButton>
                                    </>
                                )}
                                {timerState === "stopped" && (
                                    <SecondaryButton onClick={resetTimer}>
                                        Reset
                                    </SecondaryButton>
                                )}
                            </div>
                        </div>

                        {timerState === "stopped" && (
                            <div className="space-y-4">
                                <TextArea
                                    label="What was done"
                                    textSize="sm"
                                    rows={3}
                                    value={note}
                                    onChange={e => setNote(e.target.value)}
                                    placeholder="Describe what was done..."
                                />
                                <PrimaryButton
                                    onClick={handleSave}
                                    disabled={!note.trim() || saving}>
                                    {saving ? "Saving..." : "Save"}
                                </PrimaryButton>
                            </div>
                        )}
                    </>
                )}

                <hr className="border-gray-200" />

                <div className="text-sm font-medium text-gray-900">Log time manually</div>
                <ManualTimeEntryForm
                    clientId={clientId}
                    onSaved={() => {
                        invalidateEntries();
                        onDismiss();
                    }}
                />

                <hr className="border-gray-200" />

                <div className="text-sm font-medium text-gray-900">Recent entries</div>
                <RecentTimeEntries clientId={clientId} />
            </div>
        </Panel>
    );
};
